import { useMemo } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { IFilterDefinition } from '@evinced-private/ui-common';

import { DEFAULT_DATE_RANGE_DAYS } from 'src/apps/dashboard/constants/DashboardFiltersDefinitions';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { ScoreDataResult } from 'src/apps/dashboard/services/queries/score/helpers/transformResults';
import { getScoreData } from 'src/apps/dashboard/services/score/ScoreService';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { DASHBOARD_SCORE_DATA } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

export const useScoreDataQuery = (
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard,
	filterDefinitions?: IFilterDefinition[]
): UseQueryResult<ScoreDataResult, Error> => {
	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);

	const selectedPeriod = useMemo(() => {
		const dateRangeValue = effectiveFilters?.dateRange?.[0]?.value;
		return dateRangeValue ? Number(dateRangeValue) : DEFAULT_DATE_RANGE_DAYS;
	}, [effectiveFilters]);

	const { dateFrom, dateTo } = useMemo(() => {
		const now = new Date();
		const toDate = new Date(now);
		toDate.setHours(23, 59, 59, 999);

		const fromDate = new Date(now);
		fromDate.setDate(now.getDate() - (selectedPeriod - 1));
		fromDate.setHours(0, 0, 0, 0);

		return {
			dateFrom: fromDate.toISOString(),
			dateTo: toDate.toISOString()
		};
	}, [selectedPeriod]);

	return useQuery<ScoreDataResult, Error>({
		queryKey: [DASHBOARD_SCORE_DATA, selectedPeriod, effectiveFilters, labelsExcludedFromDashboard],
		queryFn: () => getScoreData(effectiveFilters, dateFrom, dateTo, selectedPeriod),
		refetchInterval: REFETCH_INTERVAL
	});
};
